
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { MINNESOTA_COUNTIES, WAIVER_TYPES, CARE_LEVELS, GENDER_OPTIONS, AGE_GROUPS, ACCESSIBILITY_OPTIONS } from '@/types/listings';
import { useToast } from '@/hooks/use-toast';

interface ListingFormProps {
  onSuccess?: () => void;
}

const ListingForm: React.FC<ListingFormProps> = ({ onSuccess }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    facility_name: '',
    care_level: '',
    waiver_types: [] as string[],
    gender_accepted: [] as string[],
    age_group: '',
    rent_amount: 0,
    location: '',
    county: '',
    beds_available: 0,
    total_beds: 0,
    licensed_capacity: 0,
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    description: '',
    amenities: [] as string[],
    accessibility: [] as string[],
  });

  const handleWaiverTypeChange = (waiver: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      waiver_types: checked 
        ? [...prev.waiver_types, waiver]
        : prev.waiver_types.filter(w => w !== waiver)
    }));
  };

  const handleGenderChange = (gender: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      gender_accepted: checked 
        ? [...prev.gender_accepted, gender]
        : prev.gender_accepted.filter(g => g !== gender)
    }));
  };

  const handleAccessibilityChange = (accessibility: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      accessibility: checked 
        ? [...prev.accessibility, accessibility]
        : prev.accessibility.filter(a => a !== accessibility)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      // Check user's subscription and listing limit
      const { data: subscriber } = await supabase
        .from('subscribers')
        .select('location_limit, additional_locations')
        .eq('user_id', user.id)
        .single();

      if (subscriber) {
        const totalLimit = subscriber.location_limit + subscriber.additional_locations;
        
        // Count existing listings
        const { count } = await supabase
          .from('bed_listings')
          .select('*', { count: 'exact', head: true })
          .eq('created_by', user.id);

        if (count && count >= totalLimit && totalLimit > 0) {
          toast({
            title: 'Listing Limit Reached',
            description: `You've reached your plan's limit of ${totalLimit} location(s). Upgrade to Tier 3 to add unlimited locations.`,
            variant: 'destructive',
          });
          
          // Send upgrade notification
          await supabase.functions.invoke('send-notification', {
            body: {
              type: 'listing_limit_reached',
              recipientEmail: user.email,
              recipientUserId: user.id,
              data: {
                currentLimit: totalLimit,
                upgradeUrl: `${window.location.origin}/subscription`,
                entityId: user.id,
                entityType: "subscription"
              }
            }
          });
          
          setLoading(false);
          return;
        }
      }

      const { error } = await supabase.from('bed_listings').insert({
        ...formData,
        created_by: user.id,
        last_updated: new Date().toISOString(),
        clicks: 0,
        clicks_reset_date: new Date().toISOString(),
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Bed listing created successfully',
      });

      onSuccess?.();
    } catch (error) {
      console.error('Error creating listing:', error);
      toast({
        title: 'Error',
        description: 'Failed to create listing. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Create New Bed Listing</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="facility_name">Facility Name</Label>
              <Input
                id="facility_name"
                value={formData.facility_name}
                onChange={(e) => setFormData({ ...formData, facility_name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="care_level">Care Level</Label>
              <Select value={formData.care_level} onValueChange={(value) => setFormData({ ...formData, care_level: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select care level" />
                </SelectTrigger>
                <SelectContent>
                  {CARE_LEVELS.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="county">County</Label>
              <Select value={formData.county} onValueChange={(value) => setFormData({ ...formData, county: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select county" />
                </SelectTrigger>
                <SelectContent>
                  {MINNESOTA_COUNTIES.map(county => (
                    <SelectItem key={county} value={county}>{county}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="age_group">Age Group</Label>
              <Select value={formData.age_group} onValueChange={(value) => setFormData({ ...formData, age_group: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select age group" />
                </SelectTrigger>
                <SelectContent>
                  {AGE_GROUPS.map(group => (
                    <SelectItem key={group} value={group}>{group}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rent_amount">Monthly Rent ($)</Label>
              <Input
                id="rent_amount"
                type="number"
                value={formData.rent_amount}
                onChange={(e) => setFormData({ ...formData, rent_amount: parseInt(e.target.value) })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location/Address</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="beds_available">Beds Available</Label>
              <Input
                id="beds_available"
                type="number"
                value={formData.beds_available}
                onChange={(e) => setFormData({ ...formData, beds_available: parseInt(e.target.value) })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="total_beds">Total Beds</Label>
              <Input
                id="total_beds"
                type="number"
                value={formData.total_beds}
                onChange={(e) => setFormData({ ...formData, total_beds: parseInt(e.target.value) })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="licensed_capacity">Licensed Capacity</Label>
              <Input
                id="licensed_capacity"
                type="number"
                value={formData.licensed_capacity}
                onChange={(e) => setFormData({ ...formData, licensed_capacity: parseInt(e.target.value) })}
                required
              />
              {formData.beds_available > formData.licensed_capacity && formData.licensed_capacity > 0 && (
                <Alert>
                  <AlertDescription>
                    Beds available cannot exceed licensed capacity.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Waiver Types Accepted</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {WAIVER_TYPES.map(waiver => (
                  <div key={waiver} className="flex items-center space-x-2">
                    <Checkbox
                      id={waiver}
                      checked={formData.waiver_types.includes(waiver)}
                      onCheckedChange={(checked) => handleWaiverTypeChange(waiver, checked as boolean)}
                    />
                    <Label htmlFor={waiver} className="text-sm">{waiver}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Gender Accepted</Label>
              <div className="flex space-x-4">
                {GENDER_OPTIONS.map(gender => (
                  <div key={gender} className="flex items-center space-x-2">
                    <Checkbox
                      id={gender}
                      checked={formData.gender_accepted.includes(gender)}
                      onCheckedChange={(checked) => handleGenderChange(gender, checked as boolean)}
                    />
                    <Label htmlFor={gender} className="text-sm">{gender}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Accessibility Features</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {ACCESSIBILITY_OPTIONS.map(feature => (
                  <div key={feature} className="flex items-center space-x-2">
                    <Checkbox
                      id={feature}
                      checked={formData.accessibility.includes(feature)}
                      onCheckedChange={(checked) => handleAccessibilityChange(feature, checked as boolean)}
                    />
                    <Label htmlFor={feature} className="text-sm">{feature}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder="Describe your facility, services, and specialties..."
              />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Creating Listing...' : 'Create Listing'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ListingForm;
